import { HANGAR, levelOf, nextCost, buy, effectText, startWeaponName } from "./hangar.js";
import { GOALS, goalName, rewardText, unlockText, unlockedWeapons, STAGE_GOALS, stageGoalName, stageGoalShort, stagePerfect } from "./goals.js";
import { t } from "../i18n/index.js";
import { writeSave } from "./save.js";

// 格納庫の画面と、結果の画面の「ずっと続く記録」の部分(DOM を作るだけ。数字の計算は hangar.js・goals.js・settle.js)。
// 文は src/i18n/ の表から(言語を切り替えたら、呼び直せば作り直される)。

const el = (tag, cls, text) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
};

function fmtTime(sec) {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

// ---- 格納庫 ----
export function renderHangar(box, save, onChange) {
  box.innerHTML = "";
  box.appendChild(el("div", "dopa-big", `${save.dopa} DOPA`));

  box.appendChild(el("div", "meta-head", t("hangar.upgrades")));
  for (const id of Object.keys(HANGAR)) {
    const u = HANGAR[id];
    const lv = levelOf(save, id);
    const cost = nextCost(save, id);
    const row = el("div", "shop-row");
    const info = el("div", "shop-info");
    info.appendChild(el("div", "shop-name", `${t(`hangar.${id}.name`)}  Lv ${lv}/${u.costs.length}`));
    info.appendChild(el("div", "shop-desc", `${t(`hangar.${id}.desc`)} ${lv ? effectText(id, lv) : "+0"}${cost != null ? ` → ${effectText(id, lv + 1)}` : ""}`));
    row.appendChild(info);
    const b = el("button", "shop-buy", cost == null ? t("hangar.max") : `${cost} DOPA`);
    b.disabled = cost == null || save.dopa < cost;
    b.addEventListener("click", () => {
      if (buy(save, id)) {
        writeSave(save);
        onChange();
      }
    });
    row.appendChild(b);
    box.appendChild(row);
  }

  box.appendChild(el("div", "meta-head", t("hangar.startWeapon")));
  const unlocked = unlockedWeapons(save);
  const picks = el("div", "start-picks");
  for (const id of [null, ...GOALS.filter((g) => g.unlock).map((g) => g.unlock)]) {
    const open = id == null || unlocked.includes(id);
    const goal = GOALS.find((g) => g.unlock === id);
    const b = el("button", "start-pick", id == null ? t("hangar.none") : open ? startWeaponName(id) : `🔒 ${goalName(goal)}`);
    b.disabled = !open;
    b.classList.toggle("on", save.startWeapon === id);
    b.addEventListener("click", () => {
      save.startWeapon = id;
      writeSave(save);
      onChange();
    });
    picks.appendChild(b);
  }
  box.appendChild(picks);

  box.appendChild(el("div", "meta-head", t("hangar.records")));
  const r = save.records;
  const rec = el("div", "records");
  for (const line of [
    t("rec.bestProgress", { v: fmtTime(r.bestProgress) }),
    t("rec.bestScore", { v: r.bestScore }),
    t("rec.bestKills", { v: r.bestKills }),
    t("rec.bestCombo", { v: r.bestCombo }),
    t("rec.runs", { runs: r.runs, clears: r.clears }),
    t("rec.totalKills", { v: r.totalKills }),
  ])
    rec.appendChild(el("div", null, line));
  box.appendChild(rec);

  const done = GOALS.filter((g) => save.goals[g.id]).length;
  box.appendChild(el("div", "meta-head", t("hangar.goals", { done, all: GOALS.length })));
  const list = el("div", "goal-list");
  for (const g of GOALS) {
    const row = el("div", `goal-row${save.goals[g.id] ? " done" : ""}`);
    row.appendChild(el("span", "goal-mark", save.goals[g.id] ? "✓" : "・"));
    row.appendChild(el("span", "goal-name", goalName(g)));
    row.appendChild(el("span", "goal-reward", rewardText(g)));
    list.appendChild(row);
  }
  box.appendChild(list);
}

// ---- 結果の画面の下半分 ----
export function renderSettlement(box, s) {
  box.innerHTML = "";
  box.appendChild(el("div", "dopa-big", `+${s.total} DOPA`));
  box.appendChild(el("div", "dopa-detail", s.earned.map((e) => `${e.kind === "goal" ? goalName(e.goal) : e.kind === "stageGoal" ? stageGoalName(e.goal) : t(`earned.${e.kind}`)}${e.mul > 1 ? ` ×${e.mul}` : ""} +${e.amount}`).join(" / ")));
  if (s.firstClear && s.mission < 6) box.appendChild(el("div", "new-record", t("result.newStage", { n: s.mission + 1 })));
  if (s.newRecords.length) box.appendChild(el("div", "new-record", t("result.newRecord", { list: s.newRecords.map((k) => t(`record.${k}`)).join(t("list.sep")) })));
  if (s.achieved.length) {
    const a = el("div", "achieved");
    a.appendChild(el("div", "achieved-head", t("result.achieved")));
    for (const g of s.achieved) {
      a.appendChild(el("div", null, goalName(g)));
      if (g.unlock) a.appendChild(el("div", "unlock", unlockText(g)));
    }
    box.appendChild(a);
  }
  // 面ごとの目標(3つ)。今回できたものは光らせる
  if (s.stageGoals) {
    box.appendChild(el("div", "meta-head", t("result.stageGoals", { n: s.mission })));
    for (const { goal, done, now, reward } of s.stageGoals) {
      const row = el("div", `goal-row stage-goal${done ? " done" : ""}${now ? " now" : ""}`);
      row.appendChild(el("span", "goal-mark", done ? "✓" : "・"));
      row.appendChild(el("span", "goal-name", stageGoalName(goal)));
      row.appendChild(el("span", "goal-reward", now ? `+${reward}` : t("goal.reward", { n: reward })));
      box.appendChild(row);
    }
  }
  if (s.next.length) {
    box.appendChild(el("div", "meta-head", t("result.nextGoals")));
    for (const { goal, near } of s.next) {
      const row = el("div", "next-goal");
      const head = el("div", "next-goal-head");
      head.appendChild(el("span", null, goalName(goal)));
      head.appendChild(el("span", "goal-reward", rewardText(goal)));
      row.appendChild(head);
      const bar = el("div", "bar near");
      const fill = el("div");
      fill.style.width = `${Math.round(near * 100)}%`;
      bar.appendChild(fill);
      row.appendChild(bar);
      box.appendChild(row);
    }
  }
  if (s.shop) {
    box.appendChild(
      el("div", `shop-hint${s.shop.affordable ? " ready" : ""}`, t(s.shop.affordable ? "shop.ready" : "shop.short", { name: t(`hangar.${s.shop.id}.name`), n: s.shop.short })),
    );
  }
}

// ---- 面を選ぶカード ----
// open: 選べる面の数。info(n): 面の中身(src/sim/missions.js の missionInfo)。onPick(n): 出撃
export function renderStageCards(box, save, open, info, onPick) {
  box.innerHTML = "";
  const count = Object.keys(save.cleared).length;
  for (let n = 1; n <= 6; n++) {
    const m = info(n);
    const locked = n > open;
    const card = el("button", `stage-card s${n}${locked ? " locked" : ""}${save.cleared[n] ? " cleared" : ""}${stagePerfect(save, n) ? " perfect" : ""}`);
    card.disabled = locked;
    card.appendChild(el("div", "sc-no", `STAGE ${n}`));
    if (locked) {
      card.appendChild(el("div", "sc-lock", "?"));
      card.appendChild(el("div", "sc-desc", t("stages.locked", { n: n - 1 })));
    } else {
      card.appendChild(el("div", "sc-name", m.name));
      card.appendChild(el("div", "sc-stars", "★".repeat(n) + "☆".repeat(6 - n)));
      card.appendChild(el("div", "sc-desc", t("stages.desc", { finale: t(`finale.${m.finale}`), min: m.minutes })));
      card.appendChild(el("div", "sc-dopa", `DOPA ×${m.dopa}`));
      // 面ごとの目標の印(3つ。できたものだけ点く)
      const pips = el("div", "sc-goals");
      for (const g of STAGE_GOALS) pips.appendChild(el("span", `sc-pip${save.stageGoals[n]?.[g.id] ? " on" : ""}`, stageGoalShort(g)));
      card.appendChild(pips);
      if (save.cleared[n]) card.appendChild(el("div", "sc-clear", `CLEAR ×${save.cleared[n]}`));
      card.addEventListener("click", () => onPick(n));
    }
    box.appendChild(card);
  }
  if (count >= 6) box.appendChild(el("div", "sc-note", t("stages.allClear")));
}
